import type { CaptureResult, Properties } from 'posthog-js';

const PAGE_LIFECYCLE_EVENTS = new Set(['$pageview', '$pageleave']);

const DYNAMIC_APP_PATHS: Array<[pattern: RegExp, route: string]> = [
  [/^\/library\/new-[^/]+\/?$/, '/library/new-:documentId'],
  [/^\/library\/[^/]+\/?$/, '/library/:documentId'],
  [/^\/cortex\/new-[^/]+\/?$/, '/cortex/new-:id'],
  [/^\/cortex\/[^/]+\/?$/, '/cortex/:id'],
  [/^\/tags\/[^/]+\/?$/, '/tags/:tagId'],
];

type PagePathProperty = [propertyName: string, rawPropertyName: string, normalizeValue: (value: string) => string];

const PAGE_PATH_PROPERTIES: PagePathProperty[] = [
  ['$pathname', 'raw_pathname', normalizeAppPath],
  ['$prev_pageview_pathname', 'raw_prev_pageview_pathname', normalizeAppPath],
  ['$session_entry_pathname', 'raw_session_entry_pathname', normalizeAppPath],
  ['$current_url', 'raw_current_url', normalizeUrlPath],
  ['$session_entry_url', 'raw_session_entry_url', normalizeUrlPath],
];

export function normalizePostHogPageEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !PAGE_LIFECYCLE_EVENTS.has(event.event)) return event;

  const properties = event.properties;
  if (!properties) return event;

  let appRoute: string | undefined;
  for (const [propertyName, rawPropertyName, normalizeValue] of PAGE_PATH_PROPERTIES) {
    const normalizedValue = normalizeProperty(properties, propertyName, rawPropertyName, normalizeValue);
    if (propertyName === '$pathname') {
      appRoute = normalizedValue;
    }
  }

  if (appRoute) {
    properties.app_route = appRoute;
  }

  return event;
}

function normalizeProperty(
  properties: Properties,
  propertyName: string,
  rawPropertyName: string,
  normalizeValue: (value: string) => string
) {
  const value = properties[propertyName];
  if (typeof value !== 'string') return;

  const normalizedValue = normalizeValue(value);
  if (normalizedValue === value) return;

  preserveRawProperty(properties, rawPropertyName, value);
  properties[propertyName] = normalizedValue;
  return normalizedValue;
}

function preserveRawProperty(properties: Properties, rawPropertyName: string, rawValue: string) {
  if (properties[rawPropertyName] === undefined) {
    properties[rawPropertyName] = rawValue;
  }
}

function normalizeAppPath(pathname: string) {
  const matchedPath = DYNAMIC_APP_PATHS.find(([pattern]) => pattern.test(pathname));
  return matchedPath?.[1] ?? pathname;
}

function normalizeUrlPath(value: string) {
  try {
    const url = new URL(value);
    const normalizedPathname = normalizeAppPath(url.pathname);
    if (normalizedPathname === url.pathname) {
      return value;
    }

    url.pathname = normalizedPathname;
    return url.toString();
  } catch {
    const [pathname, suffix = ''] = value.match(/^([^?#]+)(.*)$/)?.slice(1) ?? [];
    if (!pathname) {
      return value;
    }

    const normalizedPathname = normalizeAppPath(pathname);
    return normalizedPathname === pathname ? value : `${normalizedPathname}${suffix}`;
  }
}
